import React, { FC, useEffect, useState } from "react";
import { DialogWrapper } from "components/shared/dialog";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { useDispatch, useSelector } from "react-redux";
import { GetUrlBuilderData, SetUrlBuilderData } from "store/url-builder";
import { postAppStores } from "adapters";
import { useParams } from "react-router-dom";
import { AppStoreProvider } from "enums/appStoreProviders";
import style from "components/url-desinger/social-network/social-network.module.scss";
import { getStoreBadge } from "components/shared/app-store-list";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Input } from "components/shared/input";

const schema = yup.object().shape({
  stores: yup.array().of(
    yup.object().shape({
      storeName: yup.string().required().trim().label("App Store"),
      storeLink: yup.string().required().trim().label("URL"),
    })
  ),
});

export interface AppStoresDialogProps {
  isOpen: boolean;
  onClose(): void;
  builderType: string;
}

export const AppStoresDialog: FC<AppStoresDialogProps> = ({
  isOpen,
  onClose,
  builderType,
}) => {
  const dispatch = useDispatch();
  const builderData = useSelector(GetUrlBuilderData);
  const [loading, setLoading] = useState(false);
  const { cardId }: { cardId: string } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    name: "stores",
    control,
  });

  const addNewStore = () => append({ storeName: "", storeLink: "" });

  const removeStore = (idx: number) => remove(idx);

  const submit = (data: any) => {
    setLoading(true);

    if (Object.keys(errors).length) return;

    const stores = data.stores?.map((field: any) => {
      return {
        storeName: field.storeName,
        storeLink: field.storeLink,
      };
    });

    postAppStores(cardId, builderType, stores)
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
        onClose();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (builderData.appStores.length > 0) {
      append(builderData.appStores);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderData.appStores]);

  return (
    <DialogWrapper
      title="App. Stores"
      onSubmit={handleSubmit(submit)}
      open={isOpen}
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        else onClose();
      }}
      loading={loading}
    >
      {fields.map((store, index) => (
        <Grid container item xs={12} className="mb-3" key={store.id}>
          <Grid xs className="mr-2">
            <Controller
              control={control}
              name={`stores[${index}].storeName`}
              key={store.id}
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <Typography variant="subtitle2">Select App Store</Typography>
                  <Select
                    classes={{ root: style.socialSelect }}
                    labelId="providers-label"
                    id="providers"
                    disabled={loading}
                    value={value}
                    error={errors.stores && !!errors?.stores[index]?.storeName}
                    onChange={onChange}
                  >
                    {Object.keys(AppStoreProvider).map((key, index) => {
                      const option = (AppStoreProvider as any)[key];
                      if (option === AppStoreProvider.NONE) return null;
                      return (
                        <MenuItem value={option} key={index}>
                          <Box display="inline-flex" alignItems="center">
                            <Box marginRight="5px" display="inline-flex">
                              <img
                                src={getStoreBadge(option)}
                                alt={option}
                                width="25px"
                                height="25px"
                              />
                            </Box>
                            {option}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText error>
                    {(errors?.stores &&
                      errors.stores[index]?.storeName?.message) ||
                      ""}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid xs className="ml-2">
            <Controller
              control={control}
              name={`stores[${index}].storeLink`}
              key={store.id}
              render={({ field: { value, onChange } }) => (
                <Input
                  label="URL"
                  error={errors.stores && !!errors?.stores[index]?.storeLink}
                  helperText={
                    (errors?.stores &&
                      errors.stores[index]?.storeLink?.message) ||
                    ""
                  }
                  fullWidth
                  disabled={loading}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          <Grid
            item
            container
            alignItems={
              errors.stores &&
              (!!errors?.stores[index]?.storeName ||
                !!errors?.stores[index]?.storeLink)
                ? "center"
                : "flex-end"
            }
            xs={1}
          >
            <IconButton onClick={() => removeStore(index)} className="mb-1">
              <HighlightOffIcon color="error" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Grid item container xs={12} justifyContent="center">
        <Button onClick={addNewStore} style={{ textTransform: "none" }}>
          <AddIcon /> Add New App Store
        </Button>
      </Grid>
    </DialogWrapper>
  );
};
